import React from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Shield, Lock, Key, FileCheck, Server, Eye, Fingerprint, Globe } from 'lucide-react';

const sections = [
  {
    icon: Lock, title: 'AES-256-GCM Encryption',
    content: 'Files are encrypted client-side using the AES-256-GCM (Galois/Counter Mode) algorithm via the Web Crypto API. Each file gets a unique 256-bit key and 96-bit initialization vector (IV). GCM mode provides both confidentiality and authentication, detecting any tampering with the encrypted data. The encryption happens entirely in the browser — plaintext never leaves the client.',
  },
  {
    icon: Key, title: 'Key Management',
    content: 'Each file\'s encryption key and IV are generated using crypto.subtle.generateKey() with cryptographically secure randomness. The key material is exported, JSON-serialized, and stored in the database alongside the file metadata. This enables automatic decryption on download without requiring the user to remember keys.',
  },
  {
    icon: Fingerprint, title: 'Passwordless OTP Authentication',
    content: 'Authentication uses Supabase\'s email OTP (One-Time Password) system. Users receive a time-limited verification code via email. No passwords are stored or transmitted, eliminating risks of password reuse, brute force attacks, and credential stuffing. Each OTP expires after 5 minutes.',
  },
  {
    icon: Server, title: 'Row Level Security (RLS)',
    content: 'PostgreSQL Row Level Security policies enforce data isolation at the database level. Users can only query their own files and profile data. Admin access is controlled via a SECURITY DEFINER function (is_admin()) that bypasses RLS to check role without recursive policy evaluation.',
  },
  {
    icon: Shield, title: 'Supabase Storage Policies',
    content: 'The vault-files storage bucket uses folder-based access control. Each user\'s files are stored under their UUID folder. Storage policies ensure users can only upload to, read from, and delete from their own folder. Admin policies grant broader access for management.',
  },
  {
    icon: Eye, title: 'Suspicious Activity Detection',
    content: 'The system monitors for anomalous behavior: rapid file deletions (3+ in 60 seconds), bulk downloads, failed OTP attempts, and unusual access patterns. Detected events are flagged with severity levels (low/medium/high/critical) and visible in the admin panel for review.',
  },
  {
    icon: FileCheck, title: 'File Integrity (SHA-256)',
    content: 'File hashes are computed using SHA-256 before upload and stored in the database. On download, the hash can be recomputed to verify the file was not tampered with during storage. This provides non-repudiation and integrity verification.',
  },
  {
    icon: Globe, title: 'Secure File Sharing',
    content: 'Files can be shared via time-limited tokens with optional password protection. Share links expire automatically after the configured duration (1 hour to 7 days). Password-protected shares require an additional layer of authentication before download.',
  },
];

const SecurityArchitecturePage = () => (
  <DashboardLayout>
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="security-arch-title">
          Security Architecture
        </h1>
        <p className="text-base text-muted-foreground mt-1">Technical overview of all security mechanisms used in SecureVault</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sections.map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <s.icon className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{s.content}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Data Flow Diagram</h3>
        <div className="font-mono text-xs text-muted-foreground bg-muted p-4 rounded-md overflow-x-auto whitespace-pre">{
`User Browser                    Supabase
+-----------+    OTP Email     +------------+
|  Login    | ───────────────> | Auth (OTP) |
|  Page     | <─────────────── | Verify     |
+-----------+    JWT Token     +------------+
      |                              |
      v                              v
+-----------+   Encrypted      +------------+
|  Upload   | ──── File ─────> | Storage    |
|  (AES)    |   + Metadata ──> | PostgreSQL |
+-----------+                  +------------+
      |                              |
      v                              v
+-----------+   Download +     +------------+
|  Decrypt  | <── Blob ─────── | RLS Check  |
|  (AES)    |                  | + Policies |
+-----------+                  +------------+`
        }</div>
      </div>
    </div>
  </DashboardLayout>
);

export default SecurityArchitecturePage;
